const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const workoutAPI = {
  // ===== EXERCISES =====

  fetchExercises: async (includeArchived = false) => {
    const url = includeArchived
      ? `${API_URL}/api/workouts/exercises?include_archived=true`
      : `${API_URL}/api/workouts/exercises`;

    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch exercises');
    }
    return response.json();
  },

  createExercise: async (exerciseData) => {
    const response = await fetch(`${API_URL}/api/workouts/exercises`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(exerciseData)
    });
    if (!response.ok) {
      throw new Error('Failed to create exercise');
    }
    return response.json();
  },

  updateExercise: async (exerciseId, exerciseData) => {
    const response = await fetch(`${API_URL}/api/workouts/exercises/${exerciseId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(exerciseData)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.code === 'TYPE_CHANGE_FORBIDDEN') {
        const error = new Error(errorData.message || 'Cannot change exercise type');
        error.code = 'TYPE_CHANGE_FORBIDDEN';
        error.details = errorData;
        throw error;
      }
      throw new Error('Failed to update exercise');
    }
    return response.json();
  },

  archiveExercise: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/exercises/${exerciseId}/archive`, {
      method: 'PUT',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to archive exercise');
    }
    return response.json();
  },

  unarchiveExercise: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/exercises/${exerciseId}/unarchive`, {
      method: 'PUT',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to unarchive exercise');
    }
    return response.json();
  },

  fetchArchivedExercises: async () => {
    const response = await fetch(`${API_URL}/api/workouts/exercises/archived`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch archived exercises');
    }
    return response.json();
  },

  checkExerciseHasLogs: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/exercises/${exerciseId}/has-logs`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to check exercise logs');
    }
    return response.json();
  },

  // ===== TEMPLATES =====

  fetchTemplates: async (programId = null) => {
    const url = programId
      ? `${API_URL}/api/workouts/templates?program_id=${programId}`
      : `${API_URL}/api/workouts/templates`;

    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch templates');
    }
    return response.json();
  },

  fetchTemplateById: async (templateId) => {
    const response = await fetch(`${API_URL}/api/workouts/templates/${templateId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch template');
    }
    return response.json();
  },

  createTemplate: async (templateData, programId = null) => {
    const dataToSend = programId
      ? { ...templateData, program_id: programId }
      : templateData;

    const response = await fetch(`${API_URL}/api/workouts/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(dataToSend)
    });
    if (!response.ok) {
      throw new Error('Failed to create template');
    }
    return response.json();
  },

  updateTemplate: async (templateId, templateData) => {
    const response = await fetch(`${API_URL}/api/workouts/templates/${templateId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(templateData)
    });
    if (!response.ok) {
      throw new Error('Failed to update template');
    }
    return response.json();
  },

  deleteTemplate: async (templateId) => {
    const response = await fetch(`${API_URL}/api/workouts/templates/${templateId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete template');
    }
    return response.json();
  },

  fetchAllTemplates: async () => {
    const response = await fetch(`${API_URL}/api/workouts/templates/all`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch all templates');
    }
    return response.json();
  },

  copyTemplate: async (templateId, targetProgramId = null) => {
    const response = await fetch(`${API_URL}/api/workouts/templates/${templateId}/copy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ target_program_id: targetProgramId })
    });
    if (!response.ok) {
      throw new Error('Failed to copy template');
    }
    return response.json();
  },

  // ===== PROGRAMS =====

  fetchPrograms: async () => {
    const response = await fetch(`${API_URL}/api/workouts/programs`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch programs');
    }
    return response.json();
  },

  fetchProgramById: async (programId) => {
    const response = await fetch(`${API_URL}/api/workouts/programs/${programId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch program');
    }
    return response.json();
  },

  createProgram: async (programData) => {
    const response = await fetch(`${API_URL}/api/workouts/programs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(programData)
    });
    if (!response.ok) {
      throw new Error('Failed to create program');
    }
    return response.json();
  },

  updateProgram: async (programId, programData) => {
    const response = await fetch(`${API_URL}/api/workouts/programs/${programId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(programData)
    });
    if (!response.ok) {
      throw new Error('Failed to update program');
    }
    return response.json();
  },

  deleteProgram: async (programId) => {
    const response = await fetch(`${API_URL}/api/workouts/programs/${programId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete program');
    }
    return response.json();
  },

  copyProgram: async (programId) => {
    const response = await fetch(`${API_URL}/api/workouts/programs/${programId}/copy`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to copy program');
    }
    return response.json();
  },

  // ===== ACTIVE PROGRAMS =====

  fetchActivePrograms: async () => {
    const response = await fetch(`${API_URL}/api/workouts/active-programs`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch active programs');
    }
    return response.json();
  },

  startProgram: async (programId, startDate) => {
    const response = await fetch(`${API_URL}/api/workouts/active-programs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ program_id: programId, start_date: startDate })
    });
    if (!response.ok) {
      throw new Error('Failed to start program');
    }
    return response.json();
  },

  fetchCompletedPrograms: async () => {
    const response = await fetch(`${API_URL}/api/workouts/completed-programs`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch completed programs');
    }
    return response.json();
  },

  updateProgramStartDate: async (activeProgramId, startDate) => {
    const response = await fetch(`${API_URL}/api/workouts/active-programs/${activeProgramId}/start-date`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ start_date: startDate })
    });
    if (!response.ok) {
      throw new Error('Failed to update start date');
    }
    return response.json();
  },

  endProgram: async (activeProgramId) => {
    const response = await fetch(`${API_URL}/api/workouts/active-programs/${activeProgramId}/end`, {
      method: 'PUT',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to end program');
    }
    return response.json();
  },

  shiftProgramSchedule: async (activeProgramId, shiftDays) => {
    const response = await fetch(`${API_URL}/api/workouts/active-programs/${activeProgramId}/shift-schedule`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ shift_days: shiftDays })
    });
    if (!response.ok) {
      throw new Error('Failed to shift schedule');
    }
    return response.json();
  },

  updateProgramProgress: async (activeProgramId, currentWorkoutId) => {
    const response = await fetch(`${API_URL}/api/workouts/active-programs/${activeProgramId}/progress`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ current_workout_id: currentWorkoutId })
    });
    if (!response.ok) {
      throw new Error('Failed to update program progress');
    }
    return response.json();
  },

  skipWorkout: async (activeProgramId, programWorkoutId) => {
    const response = await fetch(`${API_URL}/api/workouts/active-programs/${activeProgramId}/skip-workout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ program_workout_id: programWorkoutId })
    });
    if (!response.ok) {
      throw new Error('Failed to skip workout');
    }
    return response.json();
  },

  fetchNextWorkout: async (programId) => {
    const response = await fetch(`${API_URL}/api/workouts/programs/${programId}/next-workout`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch next workout');
    }
    return response.json();
  },

  fetchProgramWorkoutById: async (programWorkoutId) => {
    const response = await fetch(`${API_URL}/api/workouts/program-workouts/${programWorkoutId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch program workout');
    }
    return response.json();
  },

  // ===== WORKOUT LOGS =====

  logWorkout: async (workoutData) => {
    const response = await fetch(`${API_URL}/api/workouts/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(workoutData)
    });
    if (!response.ok) {
      throw new Error('Failed to log workout');
    }
    return response.json();
  },

  fetchWorkoutLogs: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.limit) queryParams.append('limit', filters.limit);

    const url = `${API_URL}/api/workouts/logs${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch workout logs');
    }
    return response.json();
  },

  fetchWorkoutLogById: async (workoutLogId) => {
    const response = await fetch(`${API_URL}/api/workouts/logs/${workoutLogId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch workout log');
    }
    return response.json();
  },

  updateWorkoutLog: async (workoutLogId, workoutData) => {
    const response = await fetch(`${API_URL}/api/workouts/logs/${workoutLogId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(workoutData)
    });
    if (!response.ok) {
      throw new Error('Failed to update workout log');
    }
    return response.json();
  },

  assignWorkoutToProgram: async (workoutLogId, programWorkoutId) => {
    const response = await fetch(`${API_URL}/api/workouts/logs/${workoutLogId}/assign-program`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ program_workout_id: programWorkoutId })
    });
    if (!response.ok) {
      throw new Error('Failed to assign workout to program');
    }
    return response.json();
  },

  deleteWorkoutLog: async (workoutLogId) => {
    const response = await fetch(`${API_URL}/api/workouts/logs/${workoutLogId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete workout log');
    }
    return response.json();
  },

  fetchExerciseHistory: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/exercise-history/${exerciseId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch exercise history');
    }
    return response.json();
  },

  fetchLastWorkoutNote: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/exercise-last-note/${exerciseId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch last workout note');
    }
    return response.json();
  },

  // ===== ANALYTICS =====

  fetchPersonalRecords: async () => {
    const response = await fetch(`${API_URL}/api/workouts/analytics/personal-records`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch personal records');
    }
    return response.json();
  },

  fetchExerciseProgressData: async (exerciseId) => {
    const response = await fetch(`${API_URL}/api/workouts/analytics/progress/${exerciseId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch progress data');
    }
    return response.json();
  },

  fetchCompletionStats: async () => {
    const response = await fetch(`${API_URL}/api/workouts/analytics/completion`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch completion stats');
    }
    return response.json();
  },

  fetchCardioData: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.cardioType && filters.cardioType !== 'all') {
      params.append('cardio_type', filters.cardioType);
    }
    if (filters.exerciseId) {
      params.append('exercise_id', filters.exerciseId);
    }
    if (filters.startDate) {
      params.append('start_date', filters.startDate);
    }
    if (filters.endDate) {
      params.append('end_date', filters.endDate);
    }
    const response = await fetch(`${API_URL}/api/workouts/analytics/cardio-data?${params}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch cardio data');
    }
    return response.json();
  },

  fetchWeeklyMileage: async () => {
    const response = await fetch(`${API_URL}/api/workouts/analytics/weekly-mileage`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch weekly mileage');
    }
    return response.json();
  },

  // ===== WORKOUT DRAFTS =====

  saveDraft: async (draftData) => {
    const response = await fetch(`${API_URL}/api/workouts/draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(draftData)
    });
    if (!response.ok) {
      throw new Error('Failed to save draft');
    }
    return response.json();
  },

  loadDraft: async () => {
    const response = await fetch(`${API_URL}/api/workouts/draft`, {
      method: 'GET',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to load draft');
    }
    return response.json();
  },

  clearDraft: async () => {
    const response = await fetch(`${API_URL}/api/workouts/draft`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to clear draft');
    }
    return response.json();
  },

  // ===== CALENDAR =====

  fetchCalendarData: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.active_program_id) queryParams.append('active_program_id', filters.active_program_id);

    const url = `${API_URL}/api/workouts/calendar${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch calendar data');
    }
    return response.json();
  },

  updateWorkoutSchedule: async (activeProgramId, programWorkoutId, scheduleData) => {
    const response = await fetch(`${API_URL}/api/workouts/calendar/workout-override`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        active_program_id: activeProgramId,
        program_workout_id: programWorkoutId,
        ...scheduleData
      })
    });
    if (!response.ok) {
      throw new Error('Failed to update workout schedule');
    }
    return response.json();
  },

  deleteWorkoutScheduleOverride: async (activeProgramId, programWorkoutId) => {
    const response = await fetch(`${API_URL}/api/workouts/calendar/workout-override`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        active_program_id: activeProgramId,
        program_workout_id: programWorkoutId
      })
    });
    if (!response.ok) {
      throw new Error('Failed to delete workout override');
    }
    return response.json();
  },

  // ===== MARATHON PACE CALCULATOR =====

  fetchMarathonCalculations: async () => {
    const response = await fetch(`${API_URL}/api/workouts/calculators/marathon`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch marathon calculations');
    }
    return response.json();
  },

  saveMarathonCalculation: async (calculationData) => {
    const response = await fetch(`${API_URL}/api/workouts/calculators/marathon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(calculationData)
    });
    if (!response.ok) {
      throw new Error('Failed to save marathon calculation');
    }
    return response.json();
  },

  deleteMarathonCalculation: async (calculationId) => {
    const response = await fetch(`${API_URL}/api/workouts/calculators/marathon/${calculationId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete marathon calculation');
    }
    return response.json();
  },

  setActiveMarathonCalculation: async (calculationId) => {
    const response = await fetch(`${API_URL}/api/workouts/calculators/marathon/${calculationId}/set-active`, {
      method: 'PUT',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to set active marathon calculation');
    }
    return response.json();
  }
};
